import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, Panel } from '../../ui';
import type { MemberRow } from './rules';

/**
 * Who has access to the board (backlog §19 P16-3.1): people here now first, with a presence dot and Follow, then
 * everyone else with their role; Share at the bottom for those who may share. While the list loads, or if it
 * cannot, the people here now are still listed.
 */
export function MembersPanel({
  rows,
  status,
  following,
  onFollow,
  onShare,
  onClose,
  toggle,
}: {
  rows: readonly MemberRow[];
  status: 'loading' | 'ready' | 'error';
  following: string | null;
  onFollow: (userId: string | null) => void;
  /** Absent for whoever may not share (viewers, guests, editors). */
  onShare?: () => void;
  onClose: () => void;
  toggle?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation();
  return (
    <Panel title={t('members.title')} closeLabel={t('members.close')} onClose={onClose} toggle={toggle} className="gap-2 max-md:inset-x-3 max-md:w-auto" >
      {status === 'loading' && <p className="text-xs text-fg-muted">{t('members.loading')}</p>}
      {status === 'error' && (
        <p role="alert" className="text-xs text-fg-muted">
          {t('members.error')}
        </p>
      )}
      <ul className="flex flex-col gap-1">
        {rows.map((r) => {
          const followed = following === r.userId;
          return (
            <li key={r.userId} className="flex min-h-11 items-center gap-2">
              <span className="relative">
                <Avatar name={r.name} color={r.color} photo={r.photo} size="md" />
                {r.active && <span role="img" aria-label={t('members.online')} className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-surface bg-success" />}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate">
                  <span data-name="">{r.name}</span>
                  {r.you && <span className="text-fg-muted"> {t('members.you')}</span>}
                </span>
                <span className="text-xs text-fg-muted">
                  {t(`members.roles.${r.role}`)}
                  {r.elsewhere && <span data-elsewhere=""> · {t('members.onPage', { name: r.elsewhere })}</span>}
                </span>
              </span>
              {r.canFollow && (
                <Button
                  size="sm"
                  variant={followed ? 'primary' : 'secondary'}
                  aria-pressed={followed}
                  aria-label={t(followed ? 'members.stopName' : 'members.followName', { name: r.name })}
                  onClick={() => onFollow(followed ? null : r.userId)}
                >
                  {t(followed ? 'members.stop' : 'members.follow')}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {onShare && (
        <Button variant="primary" className="mt-2 w-full" onClick={onShare}>
          {t('sharing.share')}
        </Button>
      )}
    </Panel>
  );
}
